import React, { useEffect, useMemo, useReducer, useRef, useState } from 'react';
import Intro from './components/Intro';
import Profile from './steps/Profile';
import SetupStep from './steps/SetupStep';
import DemoMode from './steps/DemoMode';
import JumpOffGuide from './steps/JumpOffGuide';

import styles from './styles.module.scss';
import { DEFAULT_STATE, OnboardingActionType, OnboardingContext, OnboardingStep, reducer } from './context';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useActivateAccountQuery } from 'src/api/queries/accessQueries';
import Loading from './components/Loading';
import InvalidCode from './components/InvalidCode';
import { useAuthUser, useSignIn } from 'react-auth-kit';
import { useUpdateAtom } from 'jotai/utils';
import { AuthTokenResponse } from 'src/types/TApiQueries';
import { addonsSettingsAtom } from 'src/shared/settings/addons';
import { ACTION_TYPES } from 'src/shared/settings/base';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { useUpdateOnboardingProgressMutation } from 'src/api/mutations/onboardingMutations';
import { provisionSharePresets } from './provisionSharePresets';

type OnboardingParams = {
  code?: string;
};

type DemoClaimLocationState = {
  fromDemoClaim?: boolean;
  // Set by /login/sso after a Google / Microsoft signup: name came from the
  // IdP and there is no password to set, so the Profile step is skipped;
  // everything else (Setup, demo, jump-off) runs as for any new account.
  fromSsoSignup?: boolean;
  demoClaim?: {
    status?: 'OK' | 'ALREADY_CLAIMED' | 'INVALID_TOKEN' | 'ERROR' | string;
    share_id?: string;
    share_url?: string;
    asset_count?: number;
    output_name?: string;
    message?: string;
  };
  profileSnapshot?: {
    email: string;
    firstName: string;
    lastName: string;
    newPassword: string;
  };
  orgName?: string;
  industry?: string;
  // Mirrors `DemoSignupLoginResponse.ai_assistant_available`. Threaded through
  // navigation state to keep the seed source visible/auditable; the actual
  // atom seed happens in `DemoSignup.onLoginSuccess` before this navigation,
  // so this flag is informational here.
  aiAssistantAvailable?: boolean;
};

function buildSteps(
  roleName: string,
  opts: { skipProfile?: boolean; skipDemoMode?: boolean } = {},
): OnboardingStep[] {
  const isUser = roleName === 'USER';

  return [
    ...(!opts.skipProfile ? [{ title: 'Profile', component: <Profile /> }] : []),
    // Addons and schemas are account settings. Share templates were the only
    // part a manager/creator saw here; they are now added automatically (#958).
    ...(roleName === 'ADMIN' ? [{ title: 'Setup', component: <SetupStep /> }] : []),
    ...(!isUser && !opts.skipDemoMode
      ? [{ title: 'Try Agentic DAM', component: <DemoMode />, skippable: true }]
      : []),
    { title: 'Get Started', component: <JumpOffGuide /> },
  ];
}

const Onboarding = () => {
  const history = useHistory();
  const location = useLocation<DemoClaimLocationState | undefined>();
  const auth = useAuthUser();

  const { code } = useParams<OnboardingParams>();

  const claimState = location?.state || {};
  const fromDemoClaim = !!claimState.fromDemoClaim;
  const fromSsoSignup = !!claimState.fromSsoSignup;

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const industryHint = queryParams.get('industry') || claimState.industry || undefined;

  const [activatedRole, setActivatedRole] = useState<string | null>(null);
  const [activationData, setActivationData] = useState<{ userEmail?: string; orgName?: string }>({});

  // No-code path (e.g. /onboarding after demo-claim signIn): seed from the
  // already-authenticated user instead of POSTing /api/v1/account/activate/...
  useEffect(() => {
    if (code) return;
    const u = auth();
    if (!u) {
      // User isn't signed in - bounce to login.
      history.push('/login');
      return;
    }
    setActivatedRole(u.role || 'ADMIN');
    setActivationData({
      userEmail: u.userEmail,
      orgName: claimState.orgName,
    });
  }, [code]);

  const initialSteps = useMemo(
    () =>
      activatedRole
        ? buildSteps(activatedRole, {
            skipProfile: fromDemoClaim || fromSsoSignup,
            skipDemoMode: fromDemoClaim,
          })
        : [],
    [activatedRole, fromDemoClaim, fromSsoSignup],
  );

  const [state, dispatcher] = useReducer(reducer, {
    ...DEFAULT_STATE,
    steps: [],
  });

  useEffect(() => {
    if (activatedRole && initialSteps.length > 0) {
      const payload: any = {
        steps: initialSteps,
        role: activatedRole,
        industryHint,
        ...activationData,
      };
      if (fromSsoSignup) {
        // Name/email came from the identity provider; no password exists on
        // an SSO-only account (they can add one via "forgot password").
        payload.profileCompleted = true;
        if (claimState.profileSnapshot) {
          payload.savedProfileForm = claimState.profileSnapshot;
        }
      }
      if (fromDemoClaim) {
        // The visitor already typed their name/password on /signup and just
        // ran a real demo - mark both steps complete so the wizard skips
        // straight to Setup/JumpOff.
        payload.profileCompleted = true;
        payload.demoCompleted = true;
        // The claim already put the demo's sample assets in the account.
        payload.samplesLoaded = true;
        if (claimState.profileSnapshot) {
          payload.savedPassword = claimState.profileSnapshot.newPassword;
          payload.savedProfileForm = claimState.profileSnapshot;
        }
      }
      dispatcher({ type: OnboardingActionType.INITIAL_DATA, payload });
    }
    // activationData MUST be a dependency: setActivatedRole and
    // setActivationData are set in the same callback, but on React 17
    // (legacy root) those renders are not always batched — without it this
    // effect fired once with an empty activationData and never re-ran, so
    // the invitee's email never reached the wizard context and the Profile
    // step rendered a locked, blank email field (issue #1153 finding 6).
    // INITIAL_DATA merges into state, so the re-dispatch is idempotent.
  }, [activatedRole, initialSteps, activationData]);

  // Every non-USER role gets all the share-template presets, once, as soon
  // as the wizard has a signed-in user (#958) — nobody picks them any more.
  const accessToken = useAccessToken();
  const progressMutation = useUpdateOnboardingProgressMutation();
  const sharePresetsProvisioned = useRef(false);
  useEffect(() => {
    if (sharePresetsProvisioned.current || !accessToken || !activatedRole || activatedRole === 'USER') return;
    sharePresetsProvisioned.current = true;
    provisionSharePresets(accessToken).then((created) => {
      if (created > 0) progressMutation.mutate({ checklist: { share_presets_configured: true } });
    });
  }, [accessToken, activatedRole]);

  const signIn = useSignIn();
  // See DemoSignup.tsx for the rationale: optimistically seed the addons
  // atom from the activation response so consumers (chat sidebar,
  // AppHeader/AppSidebar, useAiAgentAvailable) don't flash "AI
  // Assistant not available" while /api/v1/settings is in flight.
  const updateAddonsSettings = useUpdateAtom(addonsSettingsAtom);

  const setTokenOrThrow = (response: AuthTokenResponse) => {
    const {
      jwt,
      accessID,
      notifications,
      permissions,
      preferences,
      role_id,
      role_name,
      uploadkey,
      username,
      user_email,
      user_id,
      org_name,
      ai_assistant_available,
    } = response;

    if (
      signIn({
        token: jwt,
        expiresIn: 86400,
        tokenType: 'Bearer',
        authState: {
          isLoggedIn: true,
          accessId: accessID,
          uploadkey,
          role: role_name,
          roleId: role_id,
          permissions,
          notifications,
          preferences,
          username,
          userEmail: user_email,
          userId: user_id,
        },
      })
    ) {
      setActivatedRole(role_name);
      setActivationData({ userEmail: user_email, orgName: org_name });
      if (ai_assistant_available) {
        updateAddonsSettings({
          type: ACTION_TYPES.UPDATE_STATE,
          payload: { AI_ASSISTANT: { added: true, enabled: true } },
        });
      }
      setTokenStatus('success');
    } else {
      setTokenStatus('error');
    }
  };

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<'loading' | 'success' | 'error'>(
    code ? 'loading' : 'success',
  );

  const step = state.steps[state.currentStep - 1];

  const { isError, isLoading } = useActivateAccountQuery(
    code || '',
    {
      onSuccess: (result) => {
        setTokenOrThrow(result);
      },
    },
    {
      retry: 1,
      enabled: !!code,
    },
  );

  useEffect(() => {
    if (state.finishedOnboarding) {
      history.push('/');
    }
  }, [state]);

  if (code) {
    if (isError || tokenStatus === 'error') {
      return <InvalidCode />;
    }
    if (isLoading || tokenStatus === 'loading') {
      return <Loading />;
    }
  } else if (!activatedRole) {
    // Waiting for auth-seeded role on the /onboarding path
    return <Loading />;
  }

  if (state.steps.length === 0) {
    return <Loading />;
  }

  return (
    <OnboardingContext.Provider value={[state, dispatcher]}>
      {showOnboarding ? step?.component : <Intro onStartOnboarding={() => setShowOnboarding(true)} />}
    </OnboardingContext.Provider>
  );
};

export default Onboarding;
